import {Component, Input, OnChanges, OnDestroy, SimpleChanges} from '@angular/core';
import {NumberAnimationOptions} from '../../interfaces/biozoom-api/app-configuration';

@Component({
  selector: 'app-animated-number',
  template: '{{ displayValue }}',
  styles: [':host { display: block; }'],
})
export class AnimatedNumberComponent implements OnChanges, OnDestroy {
  @Input() value: unknown;
  @Input() options?: NumberAnimationOptions;
  /** Changes whenever the dynamically generated element is rendered again. */
  @Input() animationKey: unknown;

  displayValue = '';
  private animationFrame?: number;

  ngOnChanges(_changes: SimpleChanges): void {
    this.cancelAnimation();

    const target = this.toFiniteNumber(this.value);
    if (target === undefined) {
      this.displayValue = this.value === null || this.value === undefined ? '' : String(this.value);
      return;
    }

    const start = this.toFiniteNumber(this.options?.startValue) ?? 0;
    const duration = this.toFiniteNumber(this.options?.duration) ?? 1000;
    const decimals = this.getDecimals(this.value, this.options?.decimals);

    this.displayValue = this.format(start, decimals);
    if (duration <= 0 || start === target) {
      this.displayValue = this.format(target, decimals);
      return;
    }

    let startedAt: number | undefined;
    const animate = (timestamp: number) => {
      startedAt ??= timestamp;
      const progress = Math.min((timestamp - startedAt) / duration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      const currentValue = start + (target - start) * easedProgress;
      this.displayValue = this.format(currentValue, decimals);

      if (progress < 1) {
        this.animationFrame = requestAnimationFrame(animate);
      } else {
        this.animationFrame = undefined;
        this.displayValue = this.format(target, decimals);
      }
    };

    this.animationFrame = requestAnimationFrame(animate);
  }

  ngOnDestroy(): void {
    this.cancelAnimation();
  }

  private cancelAnimation(): void {
    if (this.animationFrame !== undefined) {
      cancelAnimationFrame(this.animationFrame);
      this.animationFrame = undefined;
    }
  }

  private toFiniteNumber(value: unknown): number | undefined {
    if (value === '' || value === null || value === undefined) {
      return undefined;
    }
    const numberValue = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(numberValue) ? numberValue : undefined;
  }

  private getDecimals(value: unknown, configuredDecimals?: number): number {
    if (Number.isInteger(configuredDecimals) && configuredDecimals >= 0) {
      return configuredDecimals;
    }
    const match = String(value).match(/^[+-]?\d+\.(\d+)$/);
    return match ? match[1].length : 0;
  }

  private format(value: number, decimals: number): string {
    return value.toFixed(decimals);
  }
}
